import React, { useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import OrderDataStore from './order-data-state';
import TableComponent, { HeadCell } from '../../../component/table/table.component';
import i18n from '../../../i18n';
import { formatEUR } from '../../../constants/tool.fn';
import { dateFormat, formatEatTypeText } from '../../../constants/tool-order';
import OrderDetailDialog from './order-detail-dialog';
import { Order } from '../../../models';
import { CurrentOrderData } from '../../../models/fixed/data-analysis';
import { MobxObjectsManager, useManualRemove } from '../../mobx-objs/mobx-manager';
import MainPageState from '../../main/main-page-states';
import type { OrderDataFilterType } from './order-data-state';
import CardView from '../../../component/card/card-view';
import { useTranslation } from 'react-i18next';
import CommonInput from '../../../component/form/common-input.component';
import { ReactComponent as DineIcon } from '../../../static/images/svg/free_breakfast.svg';
import { ReactComponent as DeliveryIcon } from '../../../static/images/svg/ph_handbag-fill.svg';
import './scss/order-data.style.scss';

export const orderDataStore = MobxObjectsManager.get(OrderDataStore)

/** 营业分析双卡（堂食/外卖）并排时每列最小宽度（px）；容器放不下两张则第二张换行 */
const DATA_ANALYSIS_DUAL_CARD_MIN_WIDTH_PX = 800;

const OrderDataPage = observer(() => {
    useTranslation();
    /** 从主页面状态取当前餐厅，不依赖「当前营业状态」页是否打开 */
    const mainPageData = MobxObjectsManager.get(MainPageState);
    const stringId = mainPageData.currentRestaurant?.string_id || '';
    const data = orderDataStore.data
    useEffect(() => {
        orderDataStore.fetchData(stringId)
    }, [stringId])

    // 组件卸载时移除DataAnalysisStore
    useManualRemove(OrderDataStore)

    /**
     * 当前排序状态
     * @param key
     * @returns
     */
    const getSortStatus = (key: string, id: keyof CurrentOrderData) => {
        const eatInArrSort = orderDataStore.sortKeyValue[id];
        if (key === eatInArrSort?.key) {
            return eatInArrSort.value === 'ASC' ? 2 : 1
        }
        return 0
    }

    // 列表项未结堂食订单：待付总额 + 已付金额
    const headCellListEatIn: HeadCell[] = [
        {
            id: 'order_head_id',
            label: i18n.t('business_analysis_page_order_account'),
            sortStatus: getSortStatus('order_head_id', 'eatInArr'),
        },
        {
            id: 'table_name',
            label: i18n.t('business_analysis_page_table_name'),
            sortStatus: getSortStatus('table_name', 'eatInArr'),
        },
        {
            id: 'customer_num',
            label: i18n.t('immediate_number_people'),
            sortStatus: getSortStatus('customer_num', 'eatInArr'),
        },
        {
            id: 'should_amount',
            label: i18n.t('immediate_amounts_payable'),
            sortStatus: getSortStatus('should_amount', 'eatInArr'),
            renderFunction: (item) => <div>{formatEUR(+(item?.should_amount ?? 0))}</div>,
        },
        {
            id: 'paid_amount',
            label: i18n.t('immediate_paid_amount'),
            sortStatus: getSortStatus('paid_amount', 'eatInArr'),
            renderFunction: (item) => <div>{formatEUR(+(item?.paid_amount ?? 0))}</div>,
        },
        {
            id: 'order_start_time',
            label: i18n.t('immediate_table_opening_time'),
            sortStatus: getSortStatus('order_start_time', 'eatInArr'),
            renderFunction: (item) => (
                <div>
                    {dateFormat(item.order_start_time, 'HH:mm DD/MM/YYYY')}
                </div>
            )
        },
        {
            id: 'open_employee_name',
            label: i18n.t('immediate_opener'),
            sortStatus: getSortStatus('open_employee_name', 'eatInArr'),
        },
    ]
    // 列表项已结堂食订单
    const headCellListHisEatIn: HeadCell[] = [
        {
            id: 'order_head_id',
            label: i18n.t('business_analysis_page_order_account'),
            sortStatus: getSortStatus('order_head_id', 'hisEatInArr'),
        },
        {
            id: 'table_name',
            label: i18n.t('business_analysis_page_table_name'),
            sortStatus: getSortStatus('table_name', 'hisEatInArr'),
        },
        {
            id: 'customer_num',
            label: i18n.t('immediate_number_people'),
            sortStatus: getSortStatus('customer_num', 'hisEatInArr'),
        },
        {
            id: 'paid_amount',
            label: i18n.t('immediate_paid_amount'),
            sortStatus: getSortStatus('paid_amount', 'hisEatInArr'),
            renderFunction: (item) => <div>{formatEUR(+(item?.paid_amount ?? 0))}</div>,
        },
        {
            id: 'order_start_time',
            label: i18n.t('immediate_table_opening_time'),
            sortStatus: getSortStatus('order_start_time', 'hisEatInArr'),
            renderFunction: (item) => (
                <div>
                    {dateFormat(item.order_start_time, 'HH:mm DD/MM/YYYY')}
                </div>
            )
        },
        {
            id: 'order_end_time',
            label: i18n.t('immediate_statement_time'),
            sortStatus: getSortStatus('order_end_time', 'hisEatInArr'),
            renderFunction: (item) => (
                <div>
                    {dateFormat(item.order_end_time, 'HH:mm DD/MM/YYYY')}
                </div>
            )

        },
        {
            id: 'open_employee_name',
            label: i18n.t('immediate_opener'),
            sortStatus: getSortStatus('open_employee_name', 'hisEatInArr'),
        },
    ];
    // 列表项未结外卖订单：待付总额 + 已付金额
    const headCellListEatOut: HeadCell[] = [
        {
            id: 'order_head_id',
            label: i18n.t('business_analysis_page_order_account'),
            sortStatus: getSortStatus('order_head_id', 'eatOutArr'),
        },
        {
            id: 'eat_type',
            label: i18n.t('immediate_order_type'),
            sortStatus: getSortStatus('eat_type', 'eatOutArr'),
            renderFunction: (item) => (
                <div>
                    {formatEatTypeText(+(item?.eat_type || 0))}
                </div>
            )
        },
        {
            id: 'should_amount',
            label: i18n.t('immediate_amounts_payable'),
            sortStatus: getSortStatus('should_amount', 'eatOutArr'),
            renderFunction: (item) => <div>{formatEUR(+(item?.should_amount ?? 0))}</div>,
        },
        {
            id: 'paid_amount',
            label: i18n.t('immediate_paid_amount'),
            sortStatus: getSortStatus('paid_amount', 'eatOutArr'),
            renderFunction: (item) => <div>{formatEUR(+(item?.paid_amount ?? 0))}</div>,
        },
        {
            id: 'order_start_time',
            label: i18n.t('immediate_billing_time'),
            sortStatus: getSortStatus('order_start_time', 'eatOutArr'),
            renderFunction: (item) => (
                <div>
                    {dateFormat(item.order_start_time, 'HH:mm DD/MM/YYYY')}
                </div>
            )
        },
        {
            id: 'kds_time',
            label: i18n.t('immediate_due_time'),
            sortStatus: getSortStatus('kds_time', 'eatOutArr'),
            renderFunction: (item) => (
                <div>
                    {dateFormat(item.kds_time, 'HH:mm DD/MM/YYYY')}
                </div>
            )
        },
        {
            id: 'open_employee_name',
            label: i18n.t('immediate_billing_staff'),
            sortStatus: getSortStatus('open_employee_name', 'eatOutArr'),
        },
    ]
    // 列表项已结外卖订单
    const headCellListHisEatOut: HeadCell[] = [
        {
            id: 'order_head_id',
            label: i18n.t('business_analysis_page_order_account'),
            sortStatus: getSortStatus('order_head_id', 'hisEatOutArr'),
        },
        {
            id: 'eat_type',
            label: i18n.t('immediate_order_type'),
            sortStatus: getSortStatus('eat_type', 'hisEatOutArr'),
            renderFunction: (item) => (
                <div>
                    {formatEatTypeText(+(item?.eat_type || 0))}
                </div>
            )
        },
        {
            id: 'paid_amount',
            label: i18n.t('immediate_paid_amount'),
            sortStatus: getSortStatus('paid_amount', 'hisEatOutArr'),
            renderFunction: (item) => <div>{formatEUR(+(item?.paid_amount ?? 0))}</div>,
        },
        {
            id: 'order_start_time',
            label: i18n.t('immediate_billing_time'),
            sortStatus: getSortStatus('order_start_time', 'hisEatOutArr'),
            renderFunction: (item) => (
                <div>
                    {dateFormat(item.order_start_time, 'HH:mm DD/MM/YYYY')}
                </div>
            )
        },
        {
            id: 'kds_time',
            label: i18n.t('immediate_due_time'),
            sortStatus: getSortStatus('kds_time', 'hisEatOutArr'),
            renderFunction: (item) => (
                <div>
                    {dateFormat(item.kds_time, 'HH:mm DD/MM/YYYY')}
                </div>
            )
        },
        {
            id: 'order_end_time',
            label: i18n.t('immediate_statement_time'),
            sortStatus: getSortStatus('order_end_time', 'hisEatOutArr'),
            renderFunction: (item) => (
                <div>
                    {dateFormat(item.order_end_time, 'HH:mm DD/MM/YYYY')}
                </div>
            )
        },
        {
            id: 'open_employee_name',
            label: i18n.t('immediate_billing_staff'),
            sortStatus: getSortStatus('open_employee_name', 'hisEatOutArr'),
        },
    ];

    /** 当前筛选类型，驱动两列数据与表头 */
    const filterType: OrderDataFilterType = orderDataStore.filterType;
    /** 左侧（堂食）数据 key */
    const leftDataKey: keyof CurrentOrderData = filterType === 'UNSETTLED' ? 'eatInArr' : 'hisEatInArr';
    /** 右侧（外卖）数据 key */
    const rightDataKey: keyof CurrentOrderData = filterType === 'UNSETTLED' ? 'eatOutArr' : 'hisEatOutArr';

    /**
     * 打开详情弹窗
     * @param value
     */
    const handleOpenDetailDialog = (value: Order) => {
        orderDataStore.setOpenOrderDetail(true)
        orderDataStore.setOrderDetailData(value)
        orderDataStore.fetchDetailData()
    }

    /**
     * 排序
     * @param headCell
     */
    const handleSort = (headCell: HeadCell, key: keyof CurrentOrderData) => {
        let sortStatus: 'ASC' | 'DESC' | '' = ''
        switch (headCell.sortStatus) {
            case 0:
                sortStatus = 'DESC'
                break;
            case 1:
                sortStatus = 'ASC'
                break;
            case 2:
                sortStatus = ''
                break;
            default:
                sortStatus = ''
                break;
        }
        orderDataStore.setEatInArrSort({
            value: sortStatus ? sortStatus : undefined,
            key: sortStatus ? headCell.id : undefined
        }, key)
        orderDataStore.setDataSort(key as keyof CurrentOrderData)
    }

    return (
        orderDataStore.errorMessage ?
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 500
                }}>
                    <Typography variant='body1'>
                        {
                            orderDataStore.errorMessage
                        }
                    </Typography>
                </Box>
                :
                <>
                <Box className="order-data-page">
                        {/* 顶部：筛选类型  */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: '1rem',
                                mb: 2,
                                mt: 4,
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        backgroundColor: 'var(--fill-primary-200)',
                                        border: '1px solid var(--stroke-primary-300)',
                                        borderRadius: '8px',
                                        padding: '8px 16px',
                                        minHeight: 40,
                                    }}
                                >
                                    <CommonInput
                                        inputType="SELECT"
                                        value={filterType}
                                        items={[
                                            { value: 'UNSETTLED', label: 'order_data_page_filter_unsettled' },
                                            { value: 'SETTLED', label: 'order_data_page_filter_settled' },
                                        ]}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => orderDataStore.setFilterType((e?.target?.value ?? '') as OrderDataFilterType)}
                                        sx={{
                                            minWidth: 200,
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: 'transparent',
                                                border: 'none',
                                                '& fieldset': { border: 'none' },
                                                '&:hover fieldset': { border: 'none' },
                                                '&.Mui-focused fieldset': { border: 'none' },
                                            },
                                            '& .MuiSelect-select': {
                                                color: 'var(--text-dark)',
                                                fontSize: '20px',
                                                py: 0.5,
                                            },
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Box>
                        {/* 双卡：左堂食、右外卖；每卡最小宽 830px，放不下则换行；圆角 16px、阴影、标题区图标与色、卡片内筛选栏 */}
                        <Box
                            className="order-data-grid-container"
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 2,
                                mt: 0,
                            }}
                        >
                            <Box
                                className="order-data-grid-item"
                                sx={{
                                    display: 'flex',
                                    flex: `1 1 ${DATA_ANALYSIS_DUAL_CARD_MIN_WIDTH_PX}px`,
                                    minWidth: DATA_ANALYSIS_DUAL_CARD_MIN_WIDTH_PX,
                                    maxWidth: '100%',
                                    minHeight: 0,
                                }}
                            >
                                <CardView
                                    title={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', color: filterType === 'UNSETTLED' ? 'var(--func-info)' : 'var(--func-success)' }}>
                                            <DineIcon width={40} height={40} style={{ flexShrink: 0 }} />
                                            <Box component="span" sx={{ fontSize: 24, lineHeight: '32px', fontWeight: 500 }}>
                                                {filterType === 'UNSETTLED'
                                                    ? i18n.t('order_data_page_title_dine_in_unsettled')
                                                    : i18n.t('order_data_page_title_dine_in_settled')}
                                            </Box>
                                        </Box>
                                    }
                                    className="mt16 order-data-card"
                                    style={{
                                        borderRadius: 16,
                                        boxShadow: '0px 0px 4px 0px var(--overlay-800-a25)',
                                        overflow: 'hidden',
                                    }}
                                    contentClassName="order-data-card-content"
                                    contentRender={
                                        <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
                                            <TableComponent
                                                extraClass="order-data-table"
                                                isPagination
                                                allCount={data[leftDataKey].length}
                                                headCellList={filterType === 'UNSETTLED' ? headCellListEatIn : headCellListHisEatIn}
                                                list={data[leftDataKey]}
                                                showChecked={0}
                                                isTableLoading={orderDataStore.loading}
                                                handleSort={(value) => handleSort(value, leftDataKey)}
                                                onRowClick={handleOpenDetailDialog}
                                            />
                                        </Box>
                                    }
                                />
                            </Box>
                            <Box
                                className="order-data-grid-item"
                                sx={{
                                    display: 'flex',
                                    flex: `1 1 ${DATA_ANALYSIS_DUAL_CARD_MIN_WIDTH_PX}px`,
                                    minWidth: DATA_ANALYSIS_DUAL_CARD_MIN_WIDTH_PX,
                                    maxWidth: '100%',
                                    minHeight: 0,
                                }}
                            >
                                <CardView
                                    title={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', color: filterType === 'UNSETTLED' ? 'var(--func-info)' : 'var(--func-success)' }}>
                                            <DeliveryIcon width={40} height={40} style={{ flexShrink: 0 }} />
                                            <Box component="span" sx={{ fontSize: 24, lineHeight: '32px', fontWeight: 500 }}>
                                                {filterType === 'UNSETTLED'
                                                    ? i18n.t('order_data_page_title_delivery_unsettled')
                                                    : i18n.t('order_data_page_title_delivery_settled')}
                                            </Box>
                                        </Box>
                                    }
                                    actionRender={
                                        <Box className="order-data-card-filter-bar">

                                        </Box>
                                    }
                                    className="mt16 order-data-card"
                                    style={{
                                        borderRadius: 16,
                                        boxShadow: '0px 0px 4px 0px var(--overlay-800-a25)',
                                        overflow: 'hidden',
                                    }}
                                    contentClassName="order-data-card-content"
                                    contentRender={
                                        <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
                                            <TableComponent
                                                extraClass="order-data-table"
                                                isPagination
                                                allCount={data[rightDataKey].length}
                                                headCellList={filterType === 'UNSETTLED' ? headCellListEatOut : headCellListHisEatOut}
                                                list={data[rightDataKey]}
                                                showChecked={0}
                                                isTableLoading={orderDataStore.loading}
                                                handleSort={(value) => handleSort(value, rightDataKey)}
                                                onRowClick={handleOpenDetailDialog}
                                            />
                                        </Box>
                                    }
                                />
                            </Box>
                        </Box>
                    </Box>
                    {/* 未结列表为待付总额+已付；已结列表为已付单列 */}
                    <OrderDetailDialog
                        handleClose={() => orderDataStore.setOpenOrderDetail(false)}
                        open={orderDataStore.isOpenOrderDetail}
                        order={orderDataStore.orderDetailSummary}
                        list={orderDataStore.orderDetailDish}
                        isTableLoading={orderDataStore.detailLoading}
                    />
                </>
    )
})

export default OrderDataPage
